import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Plus, Calendar, MapPin, Clock, ExternalLink, Trash2, X } from "lucide-react";
import type { CalendarEvent } from "@shared/schema";

export default function CommunityCalendar() {
  const { user, isAuthenticated, isAdmin, isLoading } = useAuth();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", eventDate: "", eventTime: "", location: "", link: "" });

  const { data: events } = useQuery<CalendarEvent[]>({
    queryKey: ["/api/events"],
    queryFn: async () => {
      const res = await fetch("/api/events", { credentials: "include" });
      if (res.status === 401) { navigate("/login"); return []; }
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
    enabled: isAuthenticated,
  });

  const createEvent = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch("/api/events", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to create event");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/events"] });
      setShowNewEvent(false);
      setForm({ title: "", description: "", eventDate: "", eventTime: "", location: "", link: "" });
      toast({ title: "Event created" });
    },
  });

  const deleteEvent = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/events/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/events"] });
      toast({ title: "Event deleted" });
    },
  });

  if (isLoading) return <><Navbar /><main id="main-content" className="pt-20 min-h-screen flex items-center justify-center"><div className="text-muted-foreground" role="status" aria-live="polite">Loading...</div></main></>;
  if (!isAuthenticated) { navigate("/login"); return null; }

  const today = new Date().toISOString().split("T")[0];
  const upcoming = events?.filter((e) => e.eventDate >= today) || [];
  const past = events?.filter((e) => e.eventDate < today) || [];

  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
          <Button variant="ghost" className="mb-6 -ml-4 text-muted-foreground" onClick={() => navigate("/community")}>
            <ArrowLeft size={16} className="mr-2" aria-hidden="true" /> Community Hub
          </Button>

          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-display font-bold mb-2">Events Calendar</h1>
              <p className="text-muted-foreground">Community events, webinars, and meetups.</p>
            </div>
            {isAdmin && (
              <Button onClick={() => setShowNewEvent(true)} data-testid="button-new-event">
                <Plus size={18} className="mr-2" aria-hidden="true" /> Add Event
              </Button>
            )}
          </div>

          {showNewEvent && isAdmin && (
            <Card className="border-white/10 p-6 mb-8">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-semibold text-lg">Add New Event</h2>
                <button onClick={() => setShowNewEvent(false)} aria-label="Close new event form" className="p-3 -m-2 rounded-md hover:bg-white/5 transition-colors"><X size={18} className="text-muted-foreground" aria-hidden="true" /></button>
              </div>
              <form className="space-y-4" aria-label="Create new event" onSubmit={(e) => { e.preventDefault(); createEvent.mutate(form); }}>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="event-title">Title</Label>
                    <Input id="event-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Event title" className="bg-background border-white/10" required data-testid="input-event-title" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="event-date">Date</Label>
                    <Input id="event-date" type="date" value={form.eventDate} onChange={(e) => setForm({ ...form, eventDate: e.target.value })} className="bg-background border-white/10" required data-testid="input-event-date" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="event-time">Time</Label>
                    <Input id="event-time" value={form.eventTime} onChange={(e) => setForm({ ...form, eventTime: e.target.value })} placeholder="2:00 PM EST" className="bg-background border-white/10" data-testid="input-event-time" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="event-location">Location</Label>
                    <Input id="event-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Virtual / City" className="bg-background border-white/10" data-testid="input-event-location" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="event-description">Description</Label>
                  <Textarea id="event-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Event details..." rows={3} className="bg-background border-white/10" data-testid="input-event-description" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="event-link">Meeting Link (optional)</Label>
                  <Input id="event-link" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://zoom.us/j/... or https://meet.google.com/..." className="bg-background border-white/10" data-testid="input-event-link" />
                </div>
                <Button type="submit" disabled={createEvent.isPending} aria-busy={createEvent.isPending} data-testid="button-create-event">
                  {createEvent.isPending ? "Creating..." : "Create Event"}
                </Button>
              </form>
            </Card>
          )}

          {upcoming.length > 0 && (
            <div className="mb-12">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2"><Calendar size={18} className="text-emerald-400" aria-hidden="true" /> Upcoming Events</h2>
              <div className="space-y-4">
                {upcoming.map((event) => (
                  <EventCard key={event.id} event={event} isAdmin={isAdmin} onDelete={() => { if (confirm("Delete this event?")) deleteEvent.mutate(event.id); }} />
                ))}
              </div>
            </div>
          )}

          {past.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-4 text-muted-foreground">Past Events</h2>
              <div className="space-y-4 opacity-60">
                {past.map((event) => (
                  <EventCard key={event.id} event={event} isAdmin={isAdmin} onDelete={() => { if (confirm("Delete this event?")) deleteEvent.mutate(event.id); }} />
                ))}
              </div>
            </div>
          )}

          {(!events || events.length === 0) && (
            <div className="text-center py-16 bg-card border border-white/5 rounded-2xl">
              <Calendar size={32} className="mx-auto mb-4 text-muted-foreground" aria-hidden="true" />
              <h2 className="font-semibold mb-2">No events yet</h2>
              <p className="text-muted-foreground text-sm">Check back soon for upcoming community events.</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}

function EventCard({ event, isAdmin, onDelete }: { event: CalendarEvent; isAdmin: boolean; onDelete: () => void }) {
  const dateObj = new Date(event.eventDate + "T12:00:00");
  const month = dateObj.toLocaleDateString("en-US", { month: "short" });
  const day = dateObj.getDate();

  return (
    <Card className="border-white/5 p-5 flex items-start gap-5" role="article" aria-label={event.title} data-testid={`card-event-${event.id}`}>
      <div className="bg-primary/10 rounded-xl p-3 text-center min-w-[60px] shrink-0">
        <div className="text-xs font-medium text-primary uppercase">{month}</div>
        <div className="text-2xl font-display font-bold text-foreground">{day}</div>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold mb-1">{event.title}</h3>
        {event.description && <p className="text-sm text-muted-foreground mb-2">{event.description}</p>}
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
          {event.eventTime && <span className="flex items-center gap-1"><Clock size={12} aria-hidden="true" /> {event.eventTime}</span>}
          {event.location && <span className="flex items-center gap-1"><MapPin size={12} aria-hidden="true" /> {event.location}</span>}
          {event.link && (
            <a href={event.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline">
              <ExternalLink size={12} aria-hidden="true" /> Join Meeting <span className="sr-only">(opens in new tab)</span>
            </a>
          )}
        </div>
      </div>
      {isAdmin && (
        <Button variant="ghost" size="sm" className="text-red-400 shrink-0" aria-label={`Delete event: ${event.title}`} onClick={onDelete}>
          <Trash2 size={14} aria-hidden="true" />
        </Button>
      )}
    </Card>
  );
}
